import { get, isFunction } from 'lodash'
import React, { ReactElement } from 'react'
import styled from 'styled-components'

import Alert from '../Alert'
import { FieldPrefix } from '../Field'
import useFieldValue from '../Field/useFieldValue'
import { useFieldArray } from '../FieldArray'
import ListGroup, { ListGroupItem } from '../ListGroup'
import AddButton from './AddButton'
import FieldComponentWrapper from './FieldComponentWrapper'

const Wrapper = styled.div``

type TFieldArrayControl<TElementProps, TItem> = {
  name: string
  FieldComponent?: (props: TElementProps) => ReactElement // React.FC<TElementProps>
  newElement?: TItem
  additionalProps?: any
  // additionalProps?: Omit<
  //   TElementProps,
  //   'index' | 'name' | 'parentName' | 'newElement'
  // >
  addText?: string
  alwaysKeepFirst?: boolean
  alwaysKeepOne?: boolean
  disallowNewItems?: boolean
  disableRemoval?: boolean
  withoutPadding?: boolean
  flush?: boolean
  style?: any
  emptyText?: string | null
  hideWhenEmpty?: boolean
  onAdd?: (newElement: TItem | undefined) => Promise<TItem | undefined>
  onRemove?: (element: TItem) => Promise<boolean>
  customAddButton?: ReactElement
  innerStyle?: any
  addButtonStyle?: any
  canBeDeleted?: (element: TItem) => boolean
}

const FieldArrayControl = <TElementProps, TItem>(
  props: TFieldArrayControl<TElementProps, TItem>,
): React.ReactElement => {
  const {
    name: parentName,
    FieldComponent,
    newElement,
    additionalProps,
    addText,
    alwaysKeepFirst,
    alwaysKeepOne,
    disallowNewItems,
    disableRemoval,
    withoutPadding,
    innerStyle,
    flush,
    emptyText,
    hideWhenEmpty,
    onAdd,
    onRemove,
    customAddButton,
    addButtonStyle,
    canBeDeleted,
    ...rest
  } = props

  const values = useFieldValue(parentName)

  const { fields } = useFieldArray(parentName)

  const { push, remove } = fields

  const add = async (newCustomElement?: TItem): Promise<void> => {
    let item = newCustomElement || newElement
    if (isFunction(onAdd)) {
      item = await onAdd(newElement)
      if (!onAdd(item)) {
        return
      }
    }
    if (item) {
      push(item)
    }
  }

  // const onLocalRemove = ()

  if (hideWhenEmpty && fields.length < 1) {
    return <></>
  }

  return (
    <Wrapper {...rest}>
      <div style={{ ...innerStyle }}>
        <ListGroup variant={flush ? 'flush' : undefined}>
          {fields.map((name: string, index: number) => (
            <FieldPrefix prefix={name} key={name} overwritePrefix>
              <FieldComponentWrapper
                onRemove={
                  (canBeDeleted ? canBeDeleted(get(values, index)) : false) ||
                  disableRemoval ||
                  (alwaysKeepFirst && index === 0) ||
                  (alwaysKeepOne && fields.length <= 1)
                    ? undefined
                    : remove
                }
                index={index}
                // padRight={
                //   alwaysKeepFirst || (alwaysKeepOne && fields.length <= 1)
                // }
                withPadding={!withoutPadding}
              >
                {FieldComponent && (
                  <FieldComponent
                    {...additionalProps}
                    index={index}
                    name={name}
                    parentName={parentName}
                    newElement={newElement}
                    onRemove={remove}
                  />
                )}
              </FieldComponentWrapper>
            </FieldPrefix>
          ))}
          {fields.length < 1 && emptyText && (
            <Alert info noMargin style={addButtonStyle}>
              {emptyText ??
                i18n.t(`no_elements_available`, `Keine Elemente angegeben`)}
            </Alert>
          )}
        </ListGroup>
      </div>
      {!disallowNewItems &&
        (customAddButton ? (
          React.cloneElement(customAddButton, { onAdd: add })
        ) : (
          <AddButton
            onAdd={() => add()}
            label={addText}
            style={addButtonStyle}
          />
        ))}
    </Wrapper>
  )
}

export default FieldArrayControl
